/**
 * Leave encashment and year-end processing — policy, HR's year-end run, and the employee's
 * encashment request (ADR 0031).
 *
 * A SEPARATE ROUTER, SAME `/leave` PREFIX as `leave.contract.ts` and `leave-balance-admin.contract.ts`,
 * with no path overlap. Contract only: the handlers arrive in follow-up changes (CLAUDE.md invariant 8).
 *
 * THREE SURFACES
 *   - POLICY: which leave types can be encashed and how a day is priced. Effective-dated; no row
 *     means "not encashable, year-end exactly as before".
 *   - YEAR-END RUNS: HR creates a DRAFT (a preview that writes nothing to the ledger), reviews it,
 *     then applies it — carry-forward, encashment and lapse entries are written together. Applying
 *     moves balances, so it takes an `Idempotency-Key`; a retry returns the original result.
 *     Create and apply are hidden from the AI copilot: a run is a bulk, reviewed-by-a-human act.
 *   - ENCASHMENT REQUESTS: an employee asks to be paid for days. Submitting debits the ledger at
 *     once; withdrawing or rejecting writes an `encashment_reversal`. Decided through
 *     `@hrms/workflow` — the approve/reject routes are `routeMeta(null)` because the approver is
 *     whoever the workflow assigned, exactly as in `payroll-loans.contract.ts`.
 *
 * NO TAX ARITHMETIC HERE OR IN THESE ROUTES' SHAPES: amounts are estimates for review; the payroll
 * run computes what is paid, and treats the whole sum as taxable until payroll compliance supplies
 * the Section 10(10AA) rules.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  applyLeaveYearEndRunBodySchema,
  createLeaveEncashmentRequestBodySchema,
  createLeaveYearEndRunBodySchema,
  decideLeaveEncashmentRequestBodySchema,
  discardLeaveYearEndRunBodySchema,
  leaveEncashmentPolicyListItemSchema,
  leaveEncashmentPolicySchema,
  leaveEncashmentRequestSchema,
  leaveYearEndLineSchema,
  leaveYearEndLinesQuerySchema,
  leaveYearEndRunSchema,
  listLeaveEncashmentPoliciesQuerySchema,
  listLeaveEncashmentRequestsQuerySchema,
  listLeaveYearEndRunsQuerySchema,
  setLeaveEncashmentPolicyBodySchema,
  withdrawLeaveEncashmentRequestBodySchema,
} from '../leave-encashment.js';
import { errorBodySchema, idempotencyKeySchema, paginatedSchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });

const leaveTypeParams = z.object({ leaveTypeId: uuidSchema });
const runParams = z.object({ id: uuidSchema });
const requestParams = z.object({ id: uuidSchema });

/** A run with its first page of lines — what both the draft preview and the detail read return. */
export const leaveYearEndRunDetailSchema = z.object({
  run: leaveYearEndRunSchema,
  lines: paginatedSchema(leaveYearEndLineSchema),
});
export type LeaveYearEndRunDetail = z.infer<typeof leaveYearEndRunDetailSchema>;

export const leaveEncashmentContract = c.router(
  {
    // ---- Policy -------------------------------------------------------------------------
    listEncashmentPolicies: {
      method: 'GET',
      path: '/encashment-policies',
      query: listLeaveEncashmentPoliciesQuerySchema,
      responses: {
        200: z.object({ items: z.array(leaveEncashmentPolicyListItemSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: "Every leave type with its encashment policy as of a date (null = not encashable)",
      metadata: routeMeta('leave.policy:read'),
    },

    setEncashmentPolicy: {
      method: 'PUT',
      path: '/encashment-policies/:leaveTypeId',
      pathParams: leaveTypeParams,
      body: setLeaveEncashmentPolicyBodySchema,
      responses: {
        200: leaveEncashmentPolicySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** `validFrom` at or before the open row's own — history is never rewritten. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Set a leave type’s encashment policy from a date: closes the open row, opens a new one',
      metadata: routeMeta('leave.policy:manage'),
    },

    // ---- Year-end runs ------------------------------------------------------------------
    createYearEndRun: {
      method: 'POST',
      path: '/year-end/runs',
      body: createLeaveYearEndRunBodySchema,
      responses: {
        201: leaveYearEndRunDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** An un-discarded run already exists for this entity, leave year and leave type. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Draft a year-end run: the planned carry / encash / lapse per person, writing nothing to the ledger',
      // Each line's encashment estimate is days x basic/30 (or gross/30): that person's pay (ADR 0041).
      metadata: routeMeta('leave.year_end:execute', { ai: 'hidden', salaryPin: 'others' }),
    },

    listYearEndRuns: {
      method: 'GET',
      path: '/year-end/runs',
      query: listLeaveYearEndRunsQuerySchema,
      responses: {
        200: paginatedSchema(leaveYearEndRunSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Year-end runs, newest first',
      // `estimatedEncashmentAmount` sums pay-derived per-person estimates (ADR 0041).
      metadata: routeMeta('leave.year_end:read', { salaryPin: 'others' }),
    },

    getYearEndRun: {
      method: 'GET',
      path: '/year-end/runs/:id',
      pathParams: runParams,
      query: leaveYearEndLinesQuerySchema,
      responses: {
        200: leaveYearEndRunDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One year-end run with a page of its per-person lines',
      metadata: routeMeta('leave.year_end:read', { salaryPin: 'others' }),
    },

    applyYearEndRun: {
      method: 'POST',
      path: '/year-end/runs/:id/apply',
      pathParams: runParams,
      headers: idempotencyHeadersSchema,
      body: applyLeaveYearEndRunBodySchema,
      responses: {
        200: leaveYearEndRunSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Stale `expectedVersion`, or the run is not a draft. */
        409: errorBodySchema,
        /** A balance moved since the draft (re-draft to see the difference). */
        422: errorBodySchema,
      },
      summary:
        'Apply a draft: write the carry-forward, encashment and lapse ledger entries atomically ' +
        '(leave.year_end:execute, Idempotency-Key required)',
      metadata: routeMeta('leave.year_end:execute', { ai: 'hidden', salaryPin: 'others' }),
    },

    discardYearEndRun: {
      method: 'POST',
      path: '/year-end/runs/:id/discard',
      pathParams: runParams,
      body: discardLeaveYearEndRunBodySchema,
      responses: {
        200: leaveYearEndRunSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Stale `expectedVersion`, or the run is not a draft. */
        409: errorBodySchema,
      },
      summary: 'Abandon a draft run — nothing was written to the ledger, so nothing is undone',
      metadata: routeMeta('leave.year_end:execute', { salaryPin: 'others' }),
    },

    // ---- Encashment requests ------------------------------------------------------------
    createEncashmentRequest: {
      method: 'POST',
      path: '/encashment-requests',
      headers: idempotencyHeadersSchema,
      body: createLeaveEncashmentRequestBodySchema,
      responses: {
        201: leaveEncashmentRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** More than the balance, below the policy floor, over the yearly cap, or not encashable. */
        422: errorBodySchema,
      },
      summary: 'Ask to be paid for leave days — debits the balance at once and starts the approval',
      metadata: routeMeta('leave.encashment:request'),
    },

    listEncashmentRequests: {
      method: 'GET',
      path: '/encashment-requests',
      query: listLeaveEncashmentRequestsQuerySchema,
      responses: {
        200: paginatedSchema(leaveEncashmentRequestSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Encashment requests: your own, your team’s, or (with year-end read) everyone’s',
      // `estimatedAmount` / `paidAmount` are pay-derived. `scope: 'mine'` is the caller's own and never asks; the
      // handler gates `team` / `all` (ADR 0041).
      metadata: routeMeta(null, { salaryPin: 'subject' }),
    },

    withdrawEncashmentRequest: {
      method: 'POST',
      path: '/encashment-requests/:id/withdraw',
      pathParams: requestParams,
      headers: idempotencyHeadersSchema,
      body: withdrawLeaveEncashmentRequestBodySchema,
      responses: {
        200: leaveEncashmentRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** No longer pending — once approved, only payroll can reverse it. */
        409: errorBodySchema,
      },
      summary: 'Pull a pending request back — the days return to the balance',
      metadata: routeMeta(null),
    },

    approveEncashmentRequest: {
      method: 'POST',
      path: '/encashment-requests/:id/approve',
      pathParams: requestParams,
      headers: idempotencyHeadersSchema,
      body: decideLeaveEncashmentRequestBodySchema,
      responses: {
        200: leaveEncashmentRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Approve a request — authorises the payment, does not make it (payroll does)',
      metadata: routeMeta(null),
    },

    rejectEncashmentRequest: {
      method: 'POST',
      path: '/encashment-requests/:id/reject',
      pathParams: requestParams,
      headers: idempotencyHeadersSchema,
      body: decideLeaveEncashmentRequestBodySchema,
      responses: {
        200: leaveEncashmentRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Reject a pending request — the days return to the balance',
      metadata: routeMeta(null),
    },
  },
  { pathPrefix: '/leave' },
);
